import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PencilSquareIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import { Formik, Form, Field } from 'formik'
import Modal from 'src/components/Modal'
import { IconSpinner, IconX } from 'src/components/icons'
import { Link, NavLink, Outlet, useNavigate, useParams } from 'src/navigation'
import { useIpCostCentersPerOwner } from './patent_renewals'
import { settingsCardGrid, settingsCardLinkStyle } from './settings'
import { pureCountries, supportedCurrencies } from 'src/data'
import { firstUpperCase } from 'src/utils/strings'


const initial_cost_center = (owner_id: string) => ({
    name: '', invoice_currency: 'EUR', owner_id,
    vat_number: '', address_line_1: '', address_line_2: '', postal_code: '', city: '', state: '', country: 'CH', 
    bank_name: null,
    bank_address_line: null,
    swift_code: null,
    alternative_transfer_code: null,
    iban_code: null,
    account_number: null,
 })

 const fields = [
    'vat_number', 'address_line_1', 'address_line_2', 'postal_code', 'city', 'state',
    'billing-emails', 'notification-emails',
 ]

function fillNulls(xs: string[], count: number = 4) {
  const result = [...xs]
  for (let i = xs.length; i < count; i++) {
    result.push(null)
  }
  return result
}

const _generalErrorField = "general"

function _errorField(field?: string) {
  switch(field) {
    case null:
    case undefined:
        return _generalErrorField
    case "rnot_emails": return "notification-emails"
    case "billing_emails": return "billing-emails"
    default: return field
  }
}

export function CostCenterEdit({ isAdding = false }: { isAdding?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: owner_id, ccid } = useParams()

  const { costCenters, postCostCenter, deleteCostCenter } = useIpCostCentersPerOwner(owner_id)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const costCenter = isAdding ? undefined : costCenters?.find(c => c.id === ccid)
  const initialValues = isAdding
    ? initial_cost_center(owner_id)
    : costCenter || initial_cost_center(owner_id)

  if (!isAdding && costCenter) {
    initialValues['billing-emails'] = costCenter.billing_emails.filter(Boolean).join(', ')
    initialValues['notification-emails'] = costCenter.rnot_emails.filter(Boolean).join(', ')
  }
  /*
  {"name":"asdsadfsa",
  "invoice_currency":"EUR",
  "owner_id":"afd3ebed-9081-4783-a4bc-3a04aeb15c9c",
  "vat_number":"","address_line_1":"","address_line_2":"","postal_code":"","city":"","state":"","country":"CH","bank_name":null,"bank_address_line":null,"swift_code":null,"alternative_transfer_code":null,"iban_code":null,"account_number":null,
  "billing-emails":"noah@email.com","rnot_emails":["",null,null,null],"billing_emails":["noah@email.com",null,null,null]}
  */

  return (
    <div className="rounded-lg bg-white shadow w-full sm:w-fit">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values, { resetForm, setErrors }) => {
          const rnot_emails = fillNulls((values['notification-emails'] ?? '').split(',').map(s => s.trim()))
          const billing_emails = fillNulls((values['billing-emails'] ?? '').split(',').map(s => s.trim()))
          const result = await postCostCenter({...values, rnot_emails, billing_emails})
          // @ts-ignore
          const error = result?.detail?.[0]
          if (error) {
            console.warn(error)
            const field = _errorField(error.loc?.[2])
            const errors = {[field]: error.msg ?? 'Did not work. Check logs!'}
            resetForm({values, errors})
          } else if (isAdding) {
            navigate(`../cost-centers/${result.id}`)
          } else {
            resetForm({ values })
          }
        }}
      >
        {({ dirty, isSubmitting, errors }) => (
          <Form>
            <div className="flex flex-row justify-between p-4">
              <h3>{isAdding ? t('add-cost-center-details') : t('edit-cost-center-details')}</h3>
              <Link to={isAdding ? ".." : "../.."}>
                <IconX />
              </Link>
            </div>
            <div className="grid gap-4 pt-0 p-4">
              <label>
                <div className="label mb-1">{t('cost-center-name')}</div>
                <Field name="name" className="form-input w-full" required autoFocus />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label>
                  <div className="label mb-1">{t('currency')}</div>
                  <Field name="invoice_currency" className="form-select w-full" required as="select">
                    {supportedCurrencies.map(c => (
                      <option key={c} value={c}> {c} </option>
                    ))}
                  </Field>
                </label>
                <label>
                  <div className="label mb-1">{t('country')}</div>
                  <Field name="country" className="form-select w-full" required as="select">
                    {pureCountries.map(c => (
                      <option key={c} value={c}> {c} </option>
                    ))}
                  </Field>
                </label>
              </div>
              {fields.map(field => 
                <label key={field}>
                    <div className="label mb-1">{t(field)} {(field.includes('email')) && <span className='normal-case text-slate-400 text-xs'>Comma separated list; max 4</span>}</div>
                    {field in errors && <div className='text-warn-800 text-xs pb-1'>{firstUpperCase(errors[field])}</div>}
                    <Field name={field} className="form-input w-full" />
                </label>
              )}
            </div>
            {_generalErrorField in errors && 
              /* @ts-ignore */
              <div className='text-warn-800 text-xs py-2 mx-2 px-2 border-t border-warn-400'>{firstUpperCase(errors[_generalErrorField])}</div>}
            <div className="p-4 flex flex-row-reverse gap-4">
              <button type="submit" disabled={!dirty} className="btn-primary disabled:btn-disabled">
                {isAdding ? t('add') : t('save')} {isSubmitting && <IconSpinner className="ml-1 size-4 animate-spin inline" />}
              </button>
              <Link to=".." className="btn-secondary">
                {t('cancel')}
              </Link>
              {!isAdding && (
                <button type="button" className="btn-tertiary mr-auto" onClick={() => setShowDeleteModal(true)}>
                  {t('delete')}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
      {showDeleteModal && (
        <CostCenterDelete
          name={initialValues.name}
          cancelAction={() => setShowDeleteModal(false)}
          deleteAction={async () => {
            // You may want to add deleteCostCenter logic here
            await deleteCostCenter(costCenter)
            setShowDeleteModal(false)
            navigate('..')
          }}
        />
      )}
    </div>
  )
}

function CostCenterDelete({
  name,
  cancelAction = () => {},
  deleteAction = () => {},
}: {
  name: string
  cancelAction?: () => void
  deleteAction?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Modal escAction={cancelAction} blurClick={cancelAction}>
      <div className="p-4 font-semibold">{t('delete-cost-center', { name })}</div>
      <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
        <button type="button" className="btn-warn" onClick={deleteAction}>
          {t('delete')}
        </button>
        <button type="button" className="btn-secondary" onClick={cancelAction}>
          {t('cancel')}
        </button>
      </div>
    </Modal>
  )
}

export function CostCenterList({ owner_id }: { owner_id?: string }) {
  const { t } = useTranslation()
  const { costCenters } = useIpCostCentersPerOwner(owner_id)


  return (
    <>
      <div className={settingsCardGrid}>
        <h3 className="px-1">{t('cost-centers')}</h3>
        {(costCenters ?? []).map(cc => {
          const name = `${cc.name} (${cc.country} / ${cc.invoice_currency})`
          return (
            <NavLink key={cc.id} to={`cost-centers/${cc.id}`} className={settingsCardLinkStyle}>
              <PencilSquareIcon className="h-6 w-6 shrink-0" />
              <div className="flex flex-col gap-1 min-w-0">
                <span title={name} className="text-nowrap text-ellipsis overflow-hidden min-w-0">
                  {name}
                </span>
                <span className="text-sm text-slate-500">
                  {cc.address_line_1}, {cc.postal_code}, {cc.city}
                </span>
              </div>
            </NavLink>
          )
        })}
        <Link to="cost-centers" className={settingsCardLinkStyle({ isActive: false })}>
          <PlusCircleIcon className="h-6 w-6" />
          {t('add')}
        </Link>
      </div>
      <Outlet />
    </>
  )
}